import { useEffect, useMemo, useState } from "react";
import { AlertCircle } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import type { ChaoServiceDef } from "@/services/chaoDeObra";
import { saveChaoProgressWeights } from "@/services/chaoDeObra";
import { buildEapCatalogSections } from "./eapCatalogTree";
import { getProgressWeight, groupTitle, serviceLabel } from "./eapProgressUtils";

interface Props {
  open: boolean;
  onClose: () => void;
  catalog: ChaoServiceDef[];
  /** Opcional — ponderação é global; usado só para recalcular % exibido no contexto da unidade. */
  unitId?: string;
  onSaved: (catalog: ChaoServiceDef[], generalPct?: number) => void;
}

export function EapWeightsModal({ open, onClose, catalog, unitId, onSaved }: Props) {
  const sections = useMemo(() => buildEapCatalogSections(catalog), [catalog]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const initial: Record<string, string> = {};
    for (const s of catalog) {
      if (s.isGroup) continue;
      initial[s.id] = String(getProgressWeight(s));
    }
    setDraft(initial);
    setError(null);
  }, [open, catalog]);

  const totalWeight = useMemo(() => {
    let sum = 0;
    for (const s of catalog) {
      if (s.isGroup) continue;
      const v = Number(draft[s.id]);
      if (!Number.isNaN(v) && v > 0) sum += v;
    }
    return sum;
  }, [catalog, draft]);

  const onSave = async () => {
    const weights: Record<string, number> = {};
    for (const s of catalog) {
      if (s.isGroup) continue;
      const v = Number(draft[s.id]);
      if (Number.isNaN(v) || v < 0) {
        setError(`Ponderação inválida em "${serviceLabel(s.name)}".`);
        return;
      }
      weights[s.id] = v;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await saveChaoProgressWeights(weights, unitId);
      if (!res.success || !res.serviceCatalog) {
        setError(res.error ?? "Falha ao salvar.");
        return;
      }
      onSaved(res.serviceCatalog, res.generalPct != null ? Number(res.generalPct) : undefined);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Ponderação dos serviços" size="lg">
      <p className="text-sm text-slate-600 -mt-2 mb-4">
        Ponderação global do catálogo EAP — vale para todas as obras e unidades. Defina o peso de cada conta filha na
        média do avanço geral e de cada etapa. Valores maiores têm mais influência no percentual consolidado.
      </p>

      {error && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="max-h-[min(60vh,520px)] overflow-y-auto pr-1 space-y-5">
        {sections.map((section) => {
          const leaves = [
            ...section.items,
            ...section.subgroups.flatMap((sg) => sg.items),
          ];
          if (leaves.length === 0) return null;
          return (
            <div key={section.group.id}>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">
                {groupTitle(section.group)}
              </p>
              <ul className="space-y-2">
                {leaves.map((svc) => (
                  <li
                    key={svc.id}
                    className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 rounded-xl border border-slate-100 bg-slate-50/80 px-3 py-2.5"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-800 leading-snug">{serviceLabel(svc.name)}</p>
                      {svc.code && <p className="text-[10px] text-slate-400 font-mono mt-0.5">{svc.code}</p>}
                    </div>
                    <label className="flex items-center gap-2 shrink-0">
                      <span className="text-xs font-semibold text-slate-500">Peso</span>
                      <input
                        type="number"
                        min={0}
                        step={0.1}
                        value={draft[svc.id] ?? "1"}
                        onChange={(e) => setDraft((prev) => ({ ...prev, [svc.id]: e.target.value }))}
                        className="w-20 rounded-lg border border-slate-200 px-2 py-1.5 text-sm font-semibold text-slate-800 tabular-nums focus:outline-none focus:ring-2 focus:ring-teal-500/40"
                      />
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-slate-500 mt-4">
        Soma dos pesos: <strong className="tabular-nums">{totalWeight.toLocaleString("pt-BR")}</strong> (não precisa
        totalizar 100; o sistema usa proporção relativa).
      </p>

      <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-slate-100">
        <Button variant="ghost" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={() => void onSave()} loading={saving} className="!bg-teal-600 hover:!bg-teal-700">
          Salvar ponderação
        </Button>
      </div>
    </Modal>
  );
}
